import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { supabase, isSupabaseConfigured } from './lib/supabase'
import type { User } from '@supabase/supabase-js'
import Home from './pages/Home'
import Login from './pages/Login'
import SessionPage from './pages/SessionPage'
import Journal from './pages/Journal'
import ProfilePage from './pages/Profile'
import Preparations from './pages/Preparations'
import PreparationSession from './pages/PreparationSession'
import Maintenance from './pages/Maintenance'
import HealingArcs from './pages/HealingArcs'
import HealingArcLanding from './pages/HealingArcLanding'
import HealingArcSession from './pages/HealingArcSession'
import UpgradeAwakening from './pages/UpgradeAwakening'
import Thread from './pages/Thread'
import Pattern from './pages/Pattern'
import Unsubscribed from './pages/Unsubscribed'
import Install from './pages/Install'
import { useLanguage } from './hooks/useLanguage'
import { syncTimezone } from './lib/notifications/client'

function ProtectedRoute({ user, children }: { user: User | null; children: React.ReactNode }) {
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

/** Check maintenance mode from the unauthenticated app-status Edge Function. */
async function fetchMaintenanceStatus(): Promise<{ maintenance: boolean; message: string | null }> {
  try {
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/app-status`,
      { cache: 'no-store' },
    )
    if (!res.ok) return { maintenance: false, message: null }
    return res.json()
  } catch {
    return { maintenance: false, message: null }
  }
}

export default function App() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [maintenance, setMaintenance] = useState(false)
  const [maintenanceMessage, setMaintenanceMessage] = useState<string | null>(null)
  const { loadLanguage } = useLanguage()

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setLoading(false)
      return
    }

    // Run auth check and maintenance check in parallel — both must resolve
    // before we render routes to avoid a flash of the wrong screen.
    const authPromise = supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null
      setUser(u)
      if (u) {
        loadLanguage(u.id)
        void syncTimezone(u.id) // quiet hours for notifications need the seeker's zone
      }
    })

    const maintenancePromise = fetchMaintenanceStatus().then(({ maintenance: m, message }) => {
      setMaintenance(m)
      setMaintenanceMessage(message)
    })

    Promise.all([authPromise, maintenancePromise]).then(() => setLoading(false))

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user ?? null
      setUser(u)
      if (u) {
        loadLanguage(u.id)
        void syncTimezone(u.id) // quiet hours for notifications need the seeker's zone
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  if (loading) {
    return (
      <div className="min-h-svh flex items-center justify-center bg-[#FAF7F4]">
        <div className="w-5 h-5 rounded-full border-2 border-[#3D1F62] border-t-transparent animate-spin" />
      </div>
    )
  }

  // Maintenance gate — replaces all routes. Admin users see the same screen;
  // toggle off via direct DB update or Supabase dashboard if the admin UI
  // itself becomes inaccessible.
  if (maintenance) {
    return <Maintenance message={maintenanceMessage} />
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={user ? <Home user={user} /> : <Login />} />
        <Route path="/home" element={<ProtectedRoute user={user}><Home user={user} /></ProtectedRoute>} />
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
        <Route
          path="/session"
          element={
            <ProtectedRoute user={user}>
              <SessionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/journal"
          element={
            <ProtectedRoute user={user}>
              {user ? <Journal user={user} /> : <Navigate to="/" replace />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute user={user}>
              {user ? <ProfilePage user={user} /> : <Navigate to="/" replace />}
            </ProtectedRoute>
          }
        />
        {/* Public: the one-click unsubscribe link lands here from any inbox, with no session. */}
        <Route path="/unsubscribed" element={<Unsubscribed />} />
        {/* Public: the marketing site's Android / iPhone / Computer buttons land here. */}
        <Route path="/install" element={<Install />} />
        <Route
          path="/preparations"
          element={
            <ProtectedRoute user={user}>
              <Preparations />
            </ProtectedRoute>
          }
        />
        <Route
          path="/preparation/:id"
          element={
            <ProtectedRoute user={user}>
              {user ? <PreparationSession user={user} /> : <Navigate to="/" replace />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/upgrade/awakening"
          element={
            <ProtectedRoute user={user}>
              {user ? <UpgradeAwakening user={user} /> : <Navigate to="/" replace />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/healing-arcs"
          element={
            <ProtectedRoute user={user}>
              {user ? <HealingArcs user={user} /> : <Navigate to="/" replace />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/healing-arc/:arcId"
          element={
            <ProtectedRoute user={user}>
              {user ? <HealingArcLanding user={user} /> : <Navigate to="/" replace />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/healing-arc/:arcId/session/:sessionNum"
          element={
            <ProtectedRoute user={user}>
              {user ? <HealingArcSession user={user} /> : <Navigate to="/" replace />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/thread"
          element={
            <ProtectedRoute user={user}>
              {user ? <Thread user={user} /> : <Navigate to="/" replace />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/pattern"
          element={
            <ProtectedRoute user={user}>
              {user ? <Pattern user={user} /> : <Navigate to="/" replace />}
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
